import React, { Component, Fragment } from 'react';
import { array, arrayOf, bool, func, number, shape, string } from 'prop-types';
import classNames from 'classnames';

import { FaChevronLeft } from 'react-icons/fa';
import config from '../../../config';
import {
  TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY,
  txHasBeenReceived,
  txIsCanceled,
  txIsDelivered,
  txIsDisputed,
  txIsEnquired,
  txIsPaymentExpired,
  txIsPaymentPending,
  txIsPurchased,
  txIsReceived,
  txIsCompleted,
  txIsInFirstReviewBy,
  txHasPassedPaymentPending,
} from '../../../util/transaction';
import {
  FormattedMessage,
  injectIntl,
  intlShape,
} from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import {
  ensureListing,
  ensureTransaction,
  ensureUser,
  userDisplayNameAsString,
} from '../../../util/data';
import { isMobileSafari } from '../../../util/userAgent';
import {
  OrderPanel,
  NamedLink,
  UserDisplayName,
  PaymentStatus,
  ProductsListRow,
  ProductList,
} from '../../../components';

import SendMessageForm from '../SendMessageForm/SendMessageForm';

// These are internal components that make this file more readable.
import BreakdownMaybe from './BreakdownMaybe';
import DeliveryInfoMaybe from './DeliveryInfoMaybe';
import FeedSection from './FeedSection';
import ActionButtonsMaybe from './ActionButtonsMaybe';
import PanelHeading, {
  HEADING_ENQUIRED,
  HEADING_PAYMENT_PENDING,
  HEADING_PAYMENT_EXPIRED,
  HEADING_CANCELED,
  HEADING_PURCHASED,
  HEADING_DELIVERED,
  HEADING_DISPUTED,
  HEADING_RECEIVED,
} from './PanelHeading';

import css from './TransactionPanel.module.css';
import TrackingNumberForm from '../TrackingNumberForm/TrackingNumberForm';
import TransactionStatus from '../../../components/TransactionStatus/TransactionStatus';

// Helper function to get display names for different roles
const displayNames = (currentUser, currentProvider, currentCustomer, intl) => {
  const authorDisplayName = (
    <UserDisplayName user={currentProvider} intl={intl} />
  );
  const customerDisplayName = (
    <UserDisplayName user={currentCustomer} intl={intl} />
  );

  let otherUserDisplayName = '';
  let otherUserDisplayNameString = '';
  const currentUserIsCustomer =
    currentUser.id &&
    currentCustomer.id &&
    currentUser.id.uuid === currentCustomer.id.uuid;
  const currentUserIsProvider =
    currentUser.id &&
    currentProvider.id &&
    currentUser.id.uuid === currentProvider.id.uuid;

  if (currentUserIsCustomer) {
    otherUserDisplayName = authorDisplayName;
    otherUserDisplayNameString = userDisplayNameAsString(currentProvider, '');
  } else if (currentUserIsProvider) {
    otherUserDisplayName = customerDisplayName;
    otherUserDisplayNameString = userDisplayNameAsString(currentCustomer, '');
  }

  return {
    authorDisplayName,
    customerDisplayName,
    otherUserDisplayName,
    otherUserDisplayNameString,
  };
};

export class TransactionPanelComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sendMessageFormFocused: false,
    };
    this.isMobSaf = false;
    this.sendMessageFormName = 'TransactionPanel.SendMessageForm';

    this.onSendMessageFormFocus = this.onSendMessageFormFocus.bind(this);
    this.onSendMessageFormBlur = this.onSendMessageFormBlur.bind(this);
    this.onMessageSubmit = this.onMessageSubmit.bind(this);
    this.scrollToMessage = this.scrollToMessage.bind(this);
    this.onTrackingNumberFormBlur = this.onTrackingNumberFormBlur.bind(this);
  }

  componentDidMount() {
    this.isMobSaf = isMobileSafari();
  }

  onSendMessageFormFocus() {
    this.setState({ sendMessageFormFocused: true });
    if (this.isMobSaf) {
      // Scroll to bottom
      window.scroll({
        top: document.body.scrollHeight,
        left: 0,
        behavior: 'smooth',
      });
    }
  }

  onSendMessageFormBlur() {
    this.setState({ sendMessageFormFocused: false });
  }

  onTrackingNumberFormBlur(x) {
    this.props.onSetTrackingNumber(x);
  }

  onMessageSubmit(values, form) {
    const message = values.message ? values.message.trim() : null;
    const { transaction, onSendMessage } = this.props;
    const ensuredTransaction = ensureTransaction(transaction);

    if (!message) {
      return;
    }
    onSendMessage(ensuredTransaction.id, message)
      .then(messageId => {
        form.reset();
        this.scrollToMessage(messageId);
      })
      .catch(e => {
        // Ignore, Redux handles the error
      });
  }

  scrollToMessage(messageId) {
    const selector = `#msg-${messageId.uuid}`;
    const el = document.querySelector(selector);
    if (el) {
      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  }

  doNothing(x) {}

  render() {
    const {
      rootClassName,
      className,
      currentUser,
      transaction,
      totalMessagePages,
      oldestMessagePageFetched,
      messages,
      initialMessageFailed = false,
      savePaymentMethodFailed = false,
      fetchMessagesInProgress,
      fetchMessagesError,
      sendMessageInProgress,
      sendMessageError,
      onManageDisableScrolling,
      onOpenDisputeModal,
      onOpenReviewModal,
      onShowMoreMessages,
      onGoBack,
      transactionRole,
      intl,
      markReceivedProps,
      markDeliveredProps,
      leaveReviewProps,
      onSubmitOrderRequest,
      timeSlots,
      fetchTimeSlotsError,
      nextTransitions,
      onFetchTransactionLineItems,
      lineItems,
      fetchLineItemsInProgress,
      fetchLineItemsError,
      isFullfillment,
      urls,
      transactionId,
      paymentRequired,
      shippingRequired,
    } = this.props;

    const currentTransaction = ensureTransaction(transaction);
    const currentProducts =
      currentTransaction.attributes.protectedData?.products || [];
    const digitalProducts = currentProducts.filter(
      product => product.productType === 'digital'
    );
    const serviceProducts = currentProducts.filter(
      product => product.productType === 'service'
    );
    const physicalProducts = currentProducts.filter(
      product => product.productType === 'physical' || !product.productType
    );
    const currentListing = ensureListing(currentTransaction.listing);

    const currentProvider = ensureUser(currentTransaction.provider);
    const currentCustomer = ensureUser(currentTransaction.customer);
    const isCustomer = transactionRole === 'customer';
    const isProvider = transactionRole === 'provider';
    const isBuyer = currentTransaction.customer.id.uuid === currentUser.id.uuid;
    const isEnquiry = txIsEnquired(currentTransaction);
    const listingLoaded = !!currentListing.id;
    const listingDeleted = listingLoaded && currentListing.attributes.deleted;
    const iscustomerLoaded = !!currentCustomer.id;
    const isCustomerBanned =
      iscustomerLoaded && currentCustomer.attributes.banned;
    const isCustomerDeleted =
      iscustomerLoaded && currentCustomer.attributes.deleted;
    const isProviderLoaded = !!currentProvider.id;
    const isProviderBanned =
      isProviderLoaded && currentProvider.attributes.banned;
    const isProviderDeleted =
      isProviderLoaded && currentProvider.attributes.deleted;

    const stateDataFn = tx => {
      if (txIsEnquired(tx)) {
        const transitions = Array.isArray(nextTransitions)
          ? nextTransitions.map(transition => {
              return transition.attributes.name;
            })
          : [];
        const hasCorrectNextTransition =
          transitions.length > 0 &&
          transitions.includes(TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY);
        return {
          headingState: HEADING_ENQUIRED,
          showOrderPanel:
            isCustomer && !isProviderBanned && hasCorrectNextTransition,
        };
      } else if (txIsPaymentPending(tx)) {
        return {
          headingState: HEADING_PAYMENT_PENDING,
          showDetailCardHeadings: isCustomer,
        };
      } else if (txIsPaymentExpired(tx)) {
        return {
          headingState: HEADING_PAYMENT_EXPIRED,
          showDetailCardHeadings: isCustomer,
        };
      } else if (txIsPurchased(tx)) {
        return {
          headingState: HEADING_PURCHASED,
          showDetailCardHeadings: isCustomer,
          showActionButtons: isCustomer ? false : true,
          primaryButtonProps: isCustomer ? {} : markDeliveredProps,
        };
      } else if (txIsCanceled(tx)) {
        return {
          headingState: HEADING_CANCELED,
          showDetailCardHeadings: isCustomer,
        };
      } else if (txIsDelivered(tx)) {
        const primaryButtonPropsMaybe = isCustomer
          ? { primaryButtonProps: markReceivedProps }
          : {};
        return {
          headingState: HEADING_DELIVERED,
          showDetailCardHeadings: isCustomer,
          showActionButtons: isCustomer,
          ...primaryButtonPropsMaybe,
          showDispute: isCustomer,
        };
      } else if (txIsDisputed(tx)) {
        return {
          headingState: HEADING_DISPUTED,
          showDetailCardHeadings: isCustomer,
        };
      } else if (
        txIsReceived(tx) ||
        txIsCompleted(tx) ||
        txIsInFirstReviewBy(tx, !isCustomer)
      ) {
        return {
          headingState: HEADING_RECEIVED,
          showDetailCardHeadings: isCustomer,
          showActionButtons: true,
          primaryButtonProps: leaveReviewProps,
        };
      } else if (txHasBeenReceived(tx)) {
        return {
          headingState: HEADING_RECEIVED,
          showDetailCardHeadings: isCustomer,
        };
      } else {
        return { headingState: 'unknown' };
      }
    };
    const stateData = stateDataFn(currentTransaction);

    const deletedListingTitle = intl.formatMessage({
      id: 'TransactionPanel.deletedListingTitle',
    });

    const {
      authorDisplayName,
      customerDisplayName,
      otherUserDisplayNameString,
    } = displayNames(currentUser, currentProvider, currentCustomer, intl);

    const listingTitle = currentListing.attributes.deleted
      ? deletedListingTitle
      : currentListing.attributes.title;
    const isPurchased = txIsPurchased(transaction);
    const hasPassedPayment = txHasPassedPaymentPending(transaction);

    const unitType = config.lineItemUnitType;

    const actionButtons = (
      <ActionButtonsMaybe
        showButtons={stateData.showActionButtons}
        primaryButtonProps={stateData?.primaryButtonProps}
        secondaryButtonProps={stateData?.secondaryButtonProps}
      />
    );

    const showSendMessageForm =
      !isCustomerBanned &&
      !isCustomerDeleted &&
      !isProviderBanned &&
      !isProviderDeleted;

    const sendMessagePlaceholder = intl.formatMessage(
      { id: 'TransactionPanel.sendMessagePlaceholder' },
      { name: otherUserDisplayNameString }
    );

    const sendingMessageNotAllowed = intl.formatMessage({
      id: 'TransactionPanel.sendingMessageNotAllowed',
    });

    const paymentMethodsPageLink = (
      <NamedLink name="PaymentMethodsPage">
        <FormattedMessage id="TransactionPanel.paymentMethodsPageLink" />
      </NamedLink>
    );

    const tracking = transaction.attributes.protectedData?.trackingNumber;
    const anyTrackingInfo =
      tracking && (!!tracking.number || !!tracking.link || !!tracking.provider);

    const classes = classNames(rootClassName || css.root, className);

    return (
      <div className={classes}>
        <div className={css.containerHeader}>
          <div className={css.transactionHeader}>
            {isFullfillment ? (
              <div className={css.transactionInfoContainer}>
                <div className={css.orderHeadingWithStatus}>
                  <div title={transaction.id.uuid} className={css.orderHeading}>
                    <div className={css.backButton}>
                      {' '}
                      <FaChevronLeft onClick={onGoBack}></FaChevronLeft>
                    </div>
                    <div> Order: </div>
                    <div className={css.orderNumber}>
                      #{transaction.id.uuid}
                    </div>
                  </div>
                  <div className={css.statusContainer}>
                    {isCustomer ? (
                      <TransactionStatus
                        tx={transaction}
                        intl={intl}
                      ></TransactionStatus>
                    ) : (
                      <PaymentStatus
                        tx={transaction}
                        intl={intl}
                      ></PaymentStatus>
                    )}
                  </div>
                </div>
                <p>
                  Ordered on: {transaction.attributes.createdAt.toDateString()}
                </p>
                {anyTrackingInfo ? (
                  <p>
                    Tracking number:{' '}
                    <b>
                      {!!tracking.number ? tracking.number : ''}{' '}
                      {!!tracking.provider ? '(' + tracking.provider + ')' : ''}
                    </b>
                    {!!tracking.link ? (
                      <a href={tracking.link}>{tracking.link}</a>
                    ) : null}
                  </p>
                ) : null}
                {stateData.showActionButtons && !isFullfillment ? (
                  <div className={css.callToActionButton}>
                    {isPurchased && isProvider ? <p>Fullfill Order</p> : null}
                    <div className={css.desktopActionButtons}>
                      {actionButtons}
                    </div>
                  </div>
                ) : null}

                {stateData.showActionButtons && isFullfillment ? (
                  <div className={css.desktopActionButtons}>
                    {actionButtons}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className={css.transactionInfoContainer}>
                <div className={css.orderHeadingWithStatus}>
                  <div title={transaction.id.uuid} className={css.orderHeading}>
                    <div className={css.backButton}>
                      {' '}
                      <FaChevronLeft onClick={onGoBack}></FaChevronLeft>
                    </div>
                    <div> Order: </div>
                    <div className={css.orderNumber}>
                      #{transaction.id.uuid}
                    </div>
                  </div>
                  <div className={css.statusContainer}>
                    {isCustomer ? (
                      <TransactionStatus
                        tx={transaction}
                        intl={intl}
                      ></TransactionStatus>
                    ) : (
                      <PaymentStatus
                        tx={transaction}
                        intl={intl}
                      ></PaymentStatus>
                    )}
                  </div>
                  <div>
                    <p className={css.orderDate}>
                      Ordered on:{' '}
                      {transaction.attributes.createdAt.toLocaleDateString(
                        undefined,
                        {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                        }
                      )}
                    </p>
                  </div>
                </div>
                <div style={{ padding: '24px' }}>
                  {anyTrackingInfo ? (
                    <p>
                      Tracking number:{' '}
                      <b>
                        {!!tracking.number ? tracking.number : ''}{' '}
                        {!!tracking.provider
                          ? '(' + tracking.provider + ')'
                          : ''}
                      </b>
                      {!!tracking.link ? (
                        <a href={tracking.link}>{tracking.link}</a>
                      ) : null}
                    </p>
                  ) : null}
                  {stateData.showActionButtons && !isFullfillment ? (
                    <div className={css.callToActionButton}>
                      {isPurchased && isProvider ? (
                        <p>Start preparing your order and update the status.</p>
                      ) : null}
                      <div className={css.desktopActionButtons}>
                        {actionButtons}
                      </div>
                    </div>
                  ) : null}

                  {stateData.showActionButtons && isFullfillment ? (
                    <div>{actionButtons}</div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={css.container}>
          <div className={css.txInfo}>
            

            {!isFullfillment ? (
              <div className={css.feedAndSendContainer}>
                <FeedSection
                  rootClassName={css.feedContainer}
                  currentTransaction={currentTransaction}
                  currentUser={currentUser}
                  fetchMessagesError={fetchMessagesError}
                  fetchMessagesInProgress={fetchMessagesInProgress}
                  initialMessageFailed={initialMessageFailed}
                  messages={messages}
                  oldestMessagePageFetched={oldestMessagePageFetched}
                  onOpenReviewModal={onOpenReviewModal}
                  onShowMoreMessages={() =>
                    onShowMoreMessages(currentTransaction.id)
                  }
                  totalMessagePages={totalMessagePages}
                />{' '}
                <div className={css.messageContainer}>
                {showSendMessageForm && !isFullfillment ? (
                  <SendMessageForm
                    formId={this.sendMessageFormName}
                    rootClassName={css.sendMessageForm}
                    messagePlaceholder={sendMessagePlaceholder}
                    inProgress={sendMessageInProgress}
                    sendMessageError={sendMessageError}
                    onFocus={this.onSendMessageFormFocus}
                    onBlur={this.onSendMessageFormBlur}
                    onSubmit={this.onMessageSubmit}
                    isBuyer={isBuyer}
                  />
                ) : !isFullfillment ? (
                  <div className={css.sendingMessageNotAllowed}>
                    {sendingMessageNotAllowed}
                  </div>
                ) : null}
              </div>
              </div>
            ) : null}

            {isFullfillment ? (
              <TrackingNumberForm
                onSubmit={this.doNothing}
                onBlur={this.onTrackingNumberFormBlur}
              ></TrackingNumberForm>
            ) : null}

            <div className={css.customerDetailsContainer}>
              <div className={css.bigSectionHeading}>Order Details</div>
              <div className={css.orderData}>
                <div>
                  <div className={css.sectionHeading}>
                    {isProvider ? 'Customer details' : 'Shop details'}
                  </div>
                  <div className={css.shippingInfoContent}>Profile</div>
                  <NamedLink
                    name="ProfilePage"
                    params={{
                      id: isProvider
                        ? currentCustomer.id.uuid
                        : currentProvider.id.uuid,
                    }}
                  >
                    {otherUserDisplayNameString}
                  </NamedLink>
                </div>
                <div>
                  {shippingRequired || paymentRequired ? (
                    <DeliveryInfoMaybe
                      standaloneContainer={false}
                      transaction={currentTransaction}
                      listing={currentListing}
                      billingRequired={!shippingRequired && paymentRequired}
                    />
                  ) : null}
                </div>
                {!isEnquiry ? (
                  <div>
                    <div className={css.sectionHeading}>Payment</div>
                    <div className={css.shippingInfoContent}>PayPal</div>
                  </div>
                ) : null}
              </div>
              
            </div>

          </div>

          <div className={css.asideDesktop}>
            
              <div className={css.detailCard}>
                {isEnquiry ? <h4>Enquired product</h4> : <h4></h4>}

                {stateData.showOrderPanel ? (
                  <div>
                    <OrderPanel
                      className={css.orderPanel}
                      titleClassName={css.orderTitle}
                      isOwnListing={false}
                      unitType={unitType}
                      listing={currentListing}
                      title={listingTitle}
                      author={currentProvider}
                      showDescription={false}
                      onSubmit={onSubmitOrderRequest}
                      onManageDisableScrolling={onManageDisableScrolling}
                      timeSlots={timeSlots}
                      fetchTimeSlotsError={fetchTimeSlotsError}
                      onFetchTransactionLineItems={onFetchTransactionLineItems}
                      lineItems={lineItems}
                      fetchLineItemsInProgress={fetchLineItemsInProgress}
                      fetchLineItemsError={fetchLineItemsError}
                      urls={this.props.urls}
                    />
                  </div>
                ) : null}
                {currentProducts && currentProducts.length > 0 ? (
                  <Fragment>
                    {digitalProducts && digitalProducts.length > 0 && (
                      <ProductList
                        standaloneContainer={false}
                        products={digitalProducts}
                        caption="Digital goods"
                        isPurchased={hasPassedPayment}
                        isBuyer={isBuyer}
                        urls={this.props.urls}
                        transactionId={this.props.transactionId}
                      />
                    )}
                    {serviceProducts && serviceProducts.length > 0 && (
                      <ProductList
                        standaloneContainer={false}
                        products={serviceProducts}
                        caption="Services"
                        isPurchased={hasPassedPayment}
                        isBuyer={isBuyer}
                      />
                    )}
                    {physicalProducts && physicalProducts.length > 0 && (
                      <ProductList
                        standaloneContainer={false}
                        products={physicalProducts}
                        caption="Goods out for delivery"
                        isPurchased={hasPassedPayment}
                        isBuyer={isBuyer}
                        urls={urls}
                      />
                    )}
                  </Fragment>
                ) : (
                  <div className={css.transactionInfoContainer}>
                    <h4>Items List</h4>
                    <ProductsListRow
                      price={currentListing.attributes.price}
                      productId={currentListing.id.uuid}
                      productPreviewImage={
                        currentListing.images[0].attributes.variants[
                          'square-small'
                        ].url
                      }
                      quantity={
                        transaction.attributes.lineItems.length > 0
                          ? transaction.attributes.lineItems[0].quantity.d[0]
                          : 1
                      }
                      allAttributes={
                        currentListing.attributes.publicData.attributes
                      }
                      variantType={
                        currentListing.attributes.publicData.variantType
                      }
                      productTitle={currentListing.attributes.title}
                      isDigital={
                        currentListing.attributes.publicData?.productType ===
                        'digital'
                      }
                      isLink={
                        currentListing.attributes.publicData
                          ?.digitalGoodType === 'link'
                      }
                      isPurchased={hasPassedPayment}
                      isBuyer={isBuyer}
                    />
                  </div>
                )}
                <BreakdownMaybe
                  className={css.breakdownContainer}
                  transaction={currentTransaction}
                  transactionRole={transactionRole}
                />
              </div>
            
          </div>
        </div>
      </div>
    );
  }
}

const actionButtonShape = shape({
  inProgress: bool.isRequired,
  error: propTypes.error,
  onTransition: func.isRequired,
  buttonText: string.isRequired,
  errorText: string.isRequired,
});

TransactionPanelComponent.propTypes = {
  rootClassName: string,
  className: string,

  currentUser: propTypes.currentUser,
  transaction: propTypes.transaction.isRequired,
  totalMessagePages: number.isRequired,
  oldestMessagePageFetched: number.isRequired,
  messages: arrayOf(propTypes.message).isRequired,
  initialMessageFailed: bool,
  savePaymentMethodFailed: bool,
  fetchMessagesInProgress: bool.isRequired,
  fetchMessagesError: propTypes.error,
  sendMessageInProgress: bool.isRequired,
  sendMessageError: propTypes.error,
  onManageDisableScrolling: func.isRequired,
  onOpenDisputeModal: func.isRequired,
  onOpenReviewModal: func.isRequired,
  onShowMoreMessages: func.isRequired,
  onSendMessage: func.isRequired,
  onSubmitOrderRequest: func.isRequired,
  timeSlots: arrayOf(propTypes.timeSlot),
  fetchTimeSlotsError: propTypes.error,
  nextTransitions: array,

  // Tx process transition related props
  markReceivedProps: actionButtonShape.isRequired,
  markReceivedFromPurchasedProps: actionButtonShape.isRequired,
  markDeliveredProps: actionButtonShape.isRequired,
  leaveReviewProps: actionButtonShape.isRequired,

  // line items
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from injectIntl
  intl: intlShape,
};

const TransactionPanel = injectIntl(TransactionPanelComponent);

export default TransactionPanel;
